// frontend/src/pages/settings/users-roles/UserFormModal.tsx
import { useMemo, useState } from 'react';
import { Loader2, UserPlus, X } from 'lucide-react';
import { usersApi } from './api';
import type { RoleSummary, SellerOption, UserRow } from './types';

/** Regla del servidor para el nombre de usuario (se muestra al lado del campo). */
const USERNAME_RE = /^[a-z0-9._-]{3,30}$/;
/** El rol que trabaja con un solo vendedor: se avisa para que no quede con "Todos". */
const SELLER_PARTNER_KEY = 'seller_partner';

/** Igual que el servidor: una lista vacía es lo mismo que "todos". */
const normSellers = (v: number[] | null): number[] | null => (v && v.length > 0 ? v : null);
/** Mismo conjunto de vendedores; el orden en que se eligieron no es un cambio. */
const sameSellers = (a: number[] | null, b: number[] | null): boolean => {
  const x = normSellers(a);
  const y = normSellers(b);
  if (x === null || y === null) return x === y;
  const sx = [...x].sort((p, q) => p - q);
  const sy = [...y].sort((p, q) => p - q);
  return sx.length === sy.length && sx.every((v, i) => v === sy[i]);
};

interface Props {
  /** `null` = alta; con usuario = edición. */
  user: UserRow | null;
  roles: RoleSummary[];
  sellers: SellerOption[];
  /** El que está usando la pantalla es Administrador (puede tocar y asignar el rol admin). */
  actorIsAdmin: boolean;
  /** Se está editando a sí mismo: el servidor no le deja cambiarse el rol. */
  isSelf: boolean;
  onClose: () => void;
  /** Alta hecha: si el servidor generó la contraseña, llega acá para mostrarla una vez. */
  onSaved: (result: { user: UserRow; generated_password?: string }) => void;
}

export function UserFormModal({ user, roles, sellers, actorIsAdmin, isSelf, onClose, onSaved }: Props) {
  const editing = !!user;
  const [username, setUsername] = useState(user?.username ?? '');
  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [roleId, setRoleId] = useState<number | ''>(user?.role_id ?? '');
  const [allSellers, setAllSellers] = useState(user ? user.seller_ids === null : true);
  const [sellerIds, setSellerIds] = useState<number[]>(user?.seller_ids ?? []);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Un no-admin no puede crear ni promover administradores: el rol ni se ofrece.
  const pickableRoles = useMemo(
    () => roles.filter((r) => actorIsAdmin || !r.is_admin),
    [roles, actorIsAdmin]
  );
  const selectedRole = pickableRoles.find((r) => r.id === roleId) ?? null;
  // Nadie se cambia a sí mismo el rol ni sus propios vendedores (el servidor devuelve 409).
  const selfLocked = editing && isSelf;
  const roleLocked = selfLocked;
  const showPartnerHint =
    selectedRole?.key === SELLER_PARTNER_KEY && (allSellers || sellerIds.length !== 1);

  const toggleSeller = (id: number) =>
    setSellerIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const submit = async () => {
    setError(null);
    const uname = username.trim().toLowerCase();
    if (!editing && !USERNAME_RE.test(uname)) {
      setError('El nombre de usuario debe tener entre 3 y 30 caracteres, con letras minúsculas, números, punto, guion o guion bajo.');
      return;
    }
    if (!roleId) { setError('Elegí un rol.'); return; }
    if (!allSellers && sellerIds.length === 0) {
      setError('Elegí al menos un vendedor, o marcá "Todos".');
      return;
    }
    if (!editing && password && password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    const seller_ids = allSellers ? null : sellerIds;
    // La lista de vendedores solo viaja si CAMBIÓ: mandarla igual hace que el servidor recalcule
    // el vendedor de cabecera del usuario, y editarle el nombre a alguien le movía su vendedor.
    const sellersChanged = editing && !selfLocked && !sameSellers(seller_ids, user!.seller_ids);
    setSaving(true);
    const res = editing
      ? await usersApi.update(user!.id, {
          display_name: displayName.trim() || null,
          ...(roleLocked ? {} : { role_id: roleId }),
          ...(sellersChanged ? { seller_ids } : {}),
        })
      : await usersApi.create({
          username: uname,
          display_name: displayName.trim() || null,
          role_id: roleId,
          seller_ids,
          ...(password ? { password } : {}),
        });
    setSaving(false);

    if (!res.success || !res.data) {
      setError(res.error || 'No se pudo guardar el usuario');
      return;
    }
    // La edición devuelve la fila; el alta devuelve `{ user, generated_password? }`.
    onSaved(editing ? { user: res.data as UserRow } : (res.data as { user: UserRow; generated_password?: string }));
  };

  const label = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
  const field =
    'w-full px-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)] disabled:opacity-60';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[var(--color-primary)]" />
            <h3 className="text-base font-semibold text-[var(--color-text)]">
              {editing ? `Editar ${user!.display_name || user!.username}` : 'Nuevo usuario'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-red-500/10 border border-red-500/30 text-red-600">{error}</div>
        )}

        <div className="space-y-4">
          {!editing && (
            <div>
              <label className={label}>Nombre de usuario</label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                autoFocus
                placeholder="ej: maria.lopez"
                className={field}
              />
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Entre 3 y 30 caracteres. Se permiten letras minúsculas, números, punto, guion y guion bajo (a-z 0-9 . _ -).
              </p>
            </div>
          )}

          <div>
            <label className={label}>Nombre visible</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="ej: María López"
              className={field}
            />
          </div>

          <div>
            <label className={label}>Rol</label>
            <select
              value={roleId}
              onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : '')}
              disabled={roleLocked}
              title={roleLocked ? 'No podés cambiar tu propio rol' : undefined}
              className={field}
            >
              <option value="">Elegí un rol…</option>
              {pickableRoles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {roleLocked && (
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">No podés cambiar tu propio rol.</p>
            )}
            {selectedRole?.description && !roleLocked && (
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">{selectedRole.description}</p>
            )}
          </div>

          <div>
            <label className={label}>Vendedores</label>
            <label className="flex items-center gap-2 text-sm text-[var(--color-text)] mb-2">
              <input
                type="checkbox"
                checked={allSellers}
                disabled={selfLocked}
                onChange={(e) => setAllSellers(e.target.checked)}
              />
              Todos los vendedores
            </label>
            {selfLocked && (
              <p className="mb-2 text-[11px] text-[var(--color-text-muted)]">No podés cambiar tus propios vendedores.</p>
            )}
            {!allSellers && (
              <div className="space-y-1 max-h-40 overflow-y-auto rounded-lg border border-[var(--color-border)] p-2">
                {sellers.length === 0 && (
                  <p className="text-xs text-[var(--color-text-muted)]">No hay vendedores para elegir.</p>
                )}
                {sellers.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                    <input
                      type="checkbox"
                      checked={sellerIds.includes(s.id)}
                      disabled={selfLocked}
                      onChange={() => toggleSeller(s.id)}
                    />
                    {s.nickname}
                    {!s.active && <span className="text-[10px] text-[var(--color-text-muted)]">(inactivo)</span>}
                  </label>
                ))}
              </div>
            )}
            {showPartnerHint && (
              <p className="mt-1 text-[11px] text-amber-600">
                El rol Socio de vendedor se usa para un solo vendedor: conviene elegir uno en lugar de dejar todos.
              </p>
            )}
          </div>

          {!editing && (
            <div>
              <label className={label}>Contraseña (opcional)</label>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Dejar vacío para generar una"
                className={field}
              />
              <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                Mínimo 8 caracteres. En cualquier caso, el usuario deberá cambiarla en su primer ingreso.
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </div>
      </div>
    </div>
  );
}
